import React, { useCallback, useState, memo, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { BuildPromptSteps } from "@rapid/types/flows";
import { ThumbnailContent } from "@rapid/components/file/Carousel/ThumbnailContent";
import { useFrame } from "@rapid/contexts/FrameContext";
import { MediaItemFileType } from "@rapid/types/media";
import { DisappearingHint } from "@rapid/components/DisappearingHint";

export interface Thumbnail {
    thumbnailDataUrl: string;
    id: string;
    fileId?: string;
    frameId?: string | number; // uuid for annotations, segment for predictions
}

interface ThumbnailItemProps {
    thumbnail: Thumbnail;
    mode: BuildPromptSteps;
    flowLocked: boolean;
    onMouseEnter: (thumbnail: Thumbnail) => void;
    onMouseLeave: () => void;
    onThumbnailClick?: (e: React.MouseEvent, thumbnail: Thumbnail) => void;
    onEditAnnotationClick?: (e: React.MouseEvent, thumbnail: Thumbnail) => void;
    onDeleteAnnotationClick?: (e: React.MouseEvent, thumbnail: Thumbnail) => void;
    onThumbnailDragStart?: (e: React.DragEvent, thumbnail: Thumbnail) => void;
    onThumbnailDragEnd?: () => void;
    showRelabelHint?: boolean;
    onRelabelHintDismiss?: () => void;
    labelColor?: string;
}

// Memoized thumbnail item to prevent unnecessary re-renders
const ThumbnailItem = memo(function ThumbnailItem({
    thumbnail,
    mode,
    flowLocked,
    onMouseEnter,
    onMouseLeave,
    onThumbnailClick,
    onEditAnnotationClick,
    onDeleteAnnotationClick,
    onThumbnailDragStart,
    onThumbnailDragEnd,
    showRelabelHint,
    onRelabelHintDismiss,
    labelColor
}: ThumbnailItemProps) {
    const thumbnailRef = useRef<HTMLDivElement>(null);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, right: 0 });

    // Update tooltip position when it becomes visible
    useEffect(() => {
        if (!!showRelabelHint && thumbnailRef.current) {
            const rect = thumbnailRef.current.getBoundingClientRect();
            setTooltipPosition({
                top: rect.top + rect.height / 2,
                right: window.innerWidth - rect.left + 12 // 12px offset to the left of thumbnail
            });
        }
    }, [showRelabelHint, thumbnail.id]);
    const handleMouseEnter = useCallback(() => {
        onMouseEnter(thumbnail);
    }, [onMouseEnter, thumbnail]);

    const handleDragStart = useCallback(
        (e: React.DragEvent) => {
            onThumbnailDragStart?.(e, thumbnail);
        },
        [onThumbnailDragStart, thumbnail]
    );

    const handleClick = useCallback(
        (e: React.MouseEvent) => {
            onThumbnailClick?.(e, thumbnail);
        },
        [onThumbnailClick, thumbnail]
    );

    const handleDoubleClick = useCallback(
        (e: React.MouseEvent) => {
            onEditAnnotationClick?.(e, thumbnail);
        },
        [onEditAnnotationClick, thumbnail]
    );

    const handleDelete = useCallback(
        (e: React.MouseEvent) => {
            onDeleteAnnotationClick?.(e, thumbnail);
        },
        [onDeleteAnnotationClick, thumbnail]
    );

    return (
        <>
            <div
                ref={thumbnailRef}
                className="relative"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={onMouseLeave}
                onDragStart={handleDragStart}
                onDragEnd={onThumbnailDragEnd}
                draggable={!flowLocked}
            >
                <AnimatePresence>
                    {showRelabelHint && (
                        <motion.span
                            className="pointer-events-none absolute inset-0 z-10 animate-pulse rounded-md ring-2 ring-opacity-50"
                            style={{ "--tw-ring-color": labelColor } as React.CSSProperties}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ type: "easeInOut", duration: 0.2 }}
                        />
                    )}
                </AnimatePresence>
                <ThumbnailContent
                    className={twClassNames(
                        "h-16 w-16",
                        mode === BuildPromptSteps.REVIEW_PREDICTIONS && "border-dashed",
                        flowLocked ? "pointer-events-none opacity-100" : "cursor-pointer"
                    )}
                    thumbnail={thumbnail.thumbnailDataUrl}
                    isUploading={false}
                    fileType={MediaItemFileType.IMAGE}
                    fileName="annotation"
                    title="View annotation"
                    onClick={handleClick}
                    onDoubleClick={handleDoubleClick}
                    onDelete={!flowLocked && onDeleteAnnotationClick ? handleDelete : undefined}
                    isDisabled={flowLocked}
                />
            </div>
            <DisappearingHint
                isVisible={!!showRelabelHint}
                message="Double-click to relabel box"
                position="left"
                fixedPosition={{ top: tooltipPosition.top, right: tooltipPosition.right }}
                onDismiss={onRelabelHintDismiss}
            />
        </>
    );
});

interface CardThumbnailsProps {
    name: string;
    mode: BuildPromptSteps;
    thumbnails: Thumbnail[];
    selectedObject: string | null;
    previouslySelectedObject: string | null;
    flowLocked: boolean;
    isSliderVisible?: boolean;
    onThumbnailClick?: (e: React.MouseEvent, thumbnail: Thumbnail) => void;
    onEditAnnotationClick?: (e: React.MouseEvent, thumbnail: Thumbnail) => void;
    onDeleteAnnotationClick?: (e: React.MouseEvent, thumbnail: Thumbnail) => void;
    onThumbnailDragStart?: (e: React.DragEvent, thumbnail: Thumbnail) => void;
    onThumbnailDragEnd?: () => void;
    labelColor?: string;
    showRelabelHint?: boolean;
    relabelHintThumbnailId?: string | null;
    onRelabelHintDismiss?: () => void;
}

export const CardThumbnails: React.FC<CardThumbnailsProps> = ({
    name,
    mode,
    thumbnails,
    flowLocked,
    isSliderVisible = false,
    onThumbnailClick,
    onEditAnnotationClick,
    onDeleteAnnotationClick,
    onThumbnailDragStart,
    onThumbnailDragEnd,
    labelColor,
    showRelabelHint,
    relabelHintThumbnailId,
    onRelabelHintDismiss
}) => {
    const { setHoveredAnnotationId } = useFrame();

    const handleThumbnailMouseEnter = useCallback(
        (thumbnail: Thumbnail) => {
            // Set the hovered annotation ID to enable canvas highlighting
            // The canvas will only show/highlight it if it's on the current frame
            setHoveredAnnotationId(thumbnail.id);
        },
        [setHoveredAnnotationId]
    );

    const handleThumbnailMouseLeave = useCallback(() => {
        setHoveredAnnotationId(null);
    }, [setHoveredAnnotationId]);

    return (
        <div className="flex w-full gap-2 overflow-x-auto p-4">
            {thumbnails.length > 0 ? (
                <>
                    {thumbnails.map(
                        (thumbnail, index) =>
                            thumbnail &&
                            thumbnail.thumbnailDataUrl && (
                                <ThumbnailItem
                                    key={`${name}-thumb-${thumbnail.id}-${thumbnail.fileId}`}
                                    thumbnail={thumbnail}
                                    mode={mode}
                                    flowLocked={flowLocked}
                                    onMouseEnter={handleThumbnailMouseEnter}
                                    onMouseLeave={handleThumbnailMouseLeave}
                                    onThumbnailClick={onThumbnailClick}
                                    onEditAnnotationClick={onEditAnnotationClick}
                                    onDeleteAnnotationClick={onDeleteAnnotationClick}
                                    onThumbnailDragStart={onThumbnailDragStart}
                                    onThumbnailDragEnd={onThumbnailDragEnd}
                                    showRelabelHint={
                                        showRelabelHint && relabelHintThumbnailId === thumbnail.id
                                    }
                                    onRelabelHintDismiss={onRelabelHintDismiss}
                                    labelColor={labelColor}
                                />
                            )
                    )}
                </>
            ) : (
                !(flowLocked && mode !== BuildPromptSteps.REVIEW_PREDICTIONS) && (
                    <div
                        className={twClassNames(
                            "flex h-16 w-16 flex-shrink-0 flex-col items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-100 text-tertiary"
                        )}
                    >
                        {mode === BuildPromptSteps.REVIEW_PREDICTIONS && (
                            <span className="text-center text-xs">
                                {isSliderVisible ? (
                                    <>
                                        Adjust <br /> slider
                                    </>
                                ) : (
                                    <>
                                        No <br /> objects
                                    </>
                                )}
                            </span>
                        )}
                    </div>
                )
            )}
        </div>
    );
};
